import {Badge, Tooltip, UnstyledButton} from '@mantine/core';
import React, {useCallback, useEffect, useRef} from 'react';

import {useMCPPing} from 'nav/sidepanel/mcps/useMCPPing';

/** How often to re-ping a connected server for its live status. */
const PING_POLLING_INTERVAL_MS = 5 * 60 * 1000;

interface BadgeState {
  label: string;
  /** Mantine theme color used for the badge. */
  color: 'green' | 'red' | 'yellow';
  /** When set, the badge is clickable and re-authenticates via this URL. */
  redirectUrl?: string | null;
  /** Shown on hover, e.g. a server error message on a disconnected connector. */
  tooltip?: string;
}

/**
 * Live connection status for a connected AI Connector, shown as a small badge in the connector's
 * list row. Polls the server via {@link useMCPPing}. When the connection needs re-authentication the
 * badge becomes a button that opens the auth URL in a new tab and re-checks status once it closes.
 *
 * This is intentionally a self-contained component (rather than plain view-model data) because the
 * status is live and connector-specific; the shared list row renders it as an opaque `badge` node.
 */
export default function ConnectorStatusBadge({serverId}: {serverId: string}): JSX.Element {
  const {status, isLoading, refetch} = useMCPPing({
    serverId,
    enabled: true,
    pollingInterval: PING_POLLING_INTERVAL_MS,
  });

  let state: BadgeState;
  if (isLoading && status == null) {
    state = {label: 'Checking...', color: 'yellow'};
  } else if (status?.isConnected) {
    state = {label: 'Connected', color: 'green'};
  } else if (status?.needsRedirect) {
    state = {label: 'Auth required', color: 'yellow', redirectUrl: status.redirectUrl};
  } else {
    state = {label: 'Disconnected', color: 'red', tooltip: status?.errorMessage ?? undefined};
  }

  // Holds the "is the auth tab closed yet?" poller so we can clear it on unmount and before starting
  // another one; otherwise a still-open popup (or repeated clicks) would leak intervals that later
  // call refetch after this badge has unmounted.
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPoll = useCallback(() => {
    if (pollIntervalRef.current != null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  useEffect(() => clearPoll, [clearPoll]);

  const handleAuthClick = useCallback(() => {
    if (state.redirectUrl == null) {
      return;
    }
    const authWindow = window.open(state.redirectUrl, '_blank');
    if (authWindow != null) {
      // Replace any previous poller before starting a new one so repeated clicks don't stack intervals.
      clearPoll();
      // Re-check status once the auth tab closes so the badge flips to "Connected" without a reload.
      pollIntervalRef.current = setInterval(() => {
        if (authWindow.closed) {
          clearPoll();
          refetch();
        }
      }, 500);
    }
  }, [state.redirectUrl, refetch, clearPoll]);

  const badge = (
    <Badge color={state.color} size="xs" variant="light" radius="sm">
      {state.label}
    </Badge>
  );

  if (state.redirectUrl != null) {
    return (
      <Tooltip label="Click to re-authenticate" withinPortal={true}>
        <UnstyledButton onClick={handleAuthClick} aria-label={`${state.label} - click to re-authenticate`}>
          {badge}
        </UnstyledButton>
      </Tooltip>
    );
  }

  if (state.tooltip != null) {
    return (
      <Tooltip label={state.tooltip} withinPortal={true}>
        {badge}
      </Tooltip>
    );
  }

  return badge;
}
